"use client";

import { useLayoutEffect, useRef, useState } from "react";

import { getPlpExperimentResolveAPI } from "../../api/plpApi";
import { COUNTRIES } from "../../constants/common/common";
import { isInterceptorClientErrorShape } from "../../utils/cmsBatchClientGuards";
import CookieUtil from "../../utils/storage/cookieUtil";

const EMPTY_EXPERIMENT = { campaignId: "", groupName: "", segmentId: "" };

/** Keys the resolve payload is known to return; used to tell a real body from an error shape. */
const RESOLVE_RESPONSE_KEYS = ["campaignId", "segmentId", "groupName", "geoId", "categoryUrl"];

/**
 * True when the response is a resolve payload rather than a failure.
 *
 * The interceptor resolves failures as `{ status, data }` instead of throwing, so a failed call has to
 * be spotted by shape rather than caught.
 *
 * @param {unknown} response - Value returned by `getPlpExperimentResolveAPI`.
 * @returns {boolean}
 */
export const isPlpExperimentResolveSuccess = (response) => {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return false;
  }

  if (isInterceptorClientErrorShape(response)) {
    return false;
  }

  return RESOLVE_RESPONSE_KEYS.some((key) => key in response);
};

/**
 * Resolves PLP experiment targeting in the browser, as early as React allows.
 *
 * The SSR listing response carries `campaignId` / `groupName` / `segmentId`, but Akamai can serve one
 * user's response to another, so those values are unsafe for analytics. This hook fetches them per
 * user instead.
 *
 * Nothing waits on it: events read whatever is available when they fire, so a response that lands
 * first is reported and one that does not leaves the fields empty.
 *
 * @param {Object} params
 * @param {string} params.categoryUrl - Category url, e.g. `fnp.com/gifts/daughters-day-lp`. Falsy makes no call.
 * @param {string} [params.geoId] - Catalog id.
 * @param {string} [params.email] - Logged-in user email, read from the `faul` cookie when absent.
 * @param {string} [params.userUUID] - Anonymous experiment id, read from the `ab_user_id` cookie when absent.
 * @returns {{ campaignId: string, groupName: string, segmentId: string }}
 */
const usePlpExperimentResolve = ({ categoryUrl, geoId = COUNTRIES?.INDIA, email, userUUID }) => {
  const [experiment, setExperiment] = useState(EMPTY_EXPERIMENT);

  // Identity is read at call time so a late login cannot restart the request.
  const identityRef = useRef({ email, userUUID });
  identityRef.current = { email, userUUID };

  useLayoutEffect(() => {
    if (!categoryUrl) {
      return undefined;
    }

    let isCancelled = false;

    /**
     * Fetches the user's experiment bucket for this category.
     * @returns {Promise<void>}
     */
    const resolveExperiment = async () => {
      const { email: currentEmail, userUUID: currentUserUUID } = identityRef.current;

      const response = await getPlpExperimentResolveAPI({
        categoryUrl,
        geoId,
        email: currentEmail || CookieUtil.getCookie("faul") || "",
        fnpId: currentUserUUID || CookieUtil.getCookie("ab_user_id") || "",
      });

      if (isCancelled || !isPlpExperimentResolveSuccess(response)) {
        return;
      }

      setExperiment({
        campaignId: response?.campaignId || "",
        groupName: response?.groupName || "",
        segmentId: response?.segmentId || "",
      });
    };

    resolveExperiment();

    return () => {
      isCancelled = true;
    };
  }, [categoryUrl, geoId]);

  return experiment;
};

export default usePlpExperimentResolve;
